/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  Switch,
  DatePicker,
  message,
  Popconfirm,
  Breadcrumb,
} from "antd";
import { AiOutlineUpload } from "react-icons/ai";
import { Link } from "react-router-dom";
import moment from "moment";

import {
  getPopups,
  createPopup,
  updatePopup,
  deletePopup,
  uploadFile,
} from "../../../services/cms";

const { Option } = Select;

const Popups = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getPopups();
      setRows(data.popups || []);
    } catch (e) {
      message.error("Failed to load pop-ups");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setImageFile(null);
    setImageUrl("");
    form.resetFields();
    form.setFieldsValue({ frequency: "once-per-session", isActive: true });
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r._id);
    setImageFile(null);
    setImageUrl((r.image && r.image.url) || "");
    form.setFieldsValue({
      link: r.link || "",
      frequency: r.frequency || "once-per-session",
      isActive: r.isActive !== false,
      range:
        r.startsAt && r.endsAt
          ? [moment(r.startsAt), moment(r.endsAt)]
          : undefined,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      let url = imageUrl;
      if (imageFile) url = (await uploadFile(imageFile)).url;
      if (!url) {
        message.warning("Please select an image");
        setSaving(false);
        return;
      }
      const payload = {
        image: { url },
        link: values.link || "",
        frequency: values.frequency || "once-per-session",
        isActive: values.isActive !== false,
        startsAt: values.range ? values.range[0].toISOString() : null,
        endsAt: values.range ? values.range[1].toISOString() : null,
      };
      if (editingId) await updatePopup(editingId, payload);
      else await createPopup(payload);
      message.success(`Pop-up ${editingId ? "updated" : "created"}`);
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
          "Save failed"
      );
    }
    setSaving(false);
  };

  const onDelete = async (id) => {
    try {
      await deletePopup(id);
      message.success("Pop-up deleted");
      load();
    } catch (e) {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      render: (img) =>
        img && img.url ? <img src={img.url} alt="" style={{ height: 40 }} /> : "—",
    },
    { title: "Link", dataIndex: "link" },
    { title: "Frequency", dataIndex: "frequency" },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (a) => (a === false ? "No" : "Yes"),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <div className="flex gap-4">
          <a onClick={() => openEdit(r)}>Edit</a>
          <Popconfirm title="Delete this pop-up?" onConfirm={() => onDelete(r._id)}>
            <a style={{ color: "#e3342f" }}>Delete</a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="content-panel px-4">
      <div className="mx-4">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/dashboard">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Pop-ups</Breadcrumb.Item>
        </Breadcrumb>
        <div className="flex justify-between">
          <h1 className="page-heading">Pop-ups</h1>
          <button
            onClick={openAdd}
            className="bg-[#FA6210] h-12 px-5 font-semibold hover:bg-[#15538B] rounded-md text-white"
          >
            Add Pop-up
          </button>
        </div>
        <div className="border-b -mt-4 mb-8" />
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
        />
        <p className="text-gray-500 text-sm mt-2">
          "Once per session" shows the pop-up a single time per visitor session,
          so visitors are not spammed.
        </p>
      </div>

      <Modal
        title={editingId ? "Edit Pop-up" : "Add Pop-up"}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Image">
            {imageUrl ? (
              <div className="mb-2 text-xs text-gray-500 break-all">
                Current: {imageUrl}
              </div>
            ) : null}
            <Upload
              beforeUpload={(f) => {
                setImageFile(f);
                return false;
              }}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<AiOutlineUpload />}>Select image</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="link" label="Link (external URL, optional)">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="frequency" label="Show frequency">
            <Select>
              <Option value="once-per-session">Once per session</Option>
              <Option value="once-per-day">Once per day</Option>
              <Option value="always">Every visit</Option>
            </Select>
          </Form.Item>
          <Form.Item name="range" label="Schedule (optional)">
            <DatePicker.RangePicker showTime />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Popups;
